<template>
  <div class="autocomplete-field" onkeydown="return event.key != 'Enter'">
    <b-img
      v-if="prependIcon"
      class="prepend-icon"
      :src="require('@/assets/icons/location.svg')"
    ></b-img>

    <div
      v-if="loading"
      class="input-spinner d-flex justify-content-center align-items-center"
    >
      <b-spinner variant="light"></b-spinner>
    </div>

    <b-form-input
      id="place-autocomplete"
      name="place-autocomplete"
      v-model="query"
      type="text"
      autocomplete="off"
      :placeholder="placeholder"
      :state="inputState"
      :class="inputClass"
      :style="prependIcon ? 'padding-left: 36px' : ''"
      :disabled="loading"
      aria-describedby="input-suburb-feedback"
      @input="$emit('input', query)"
      @keyup="onKeyupLocation($event)"
      @keydown.esc="selectLocation(-1)"
      @blur="hide"
      @focus="show"
    >
    </b-form-input>
    <b-form-invalid-feedback id="input-suburb-feedback">
      <!-- eslint-disable-next-line -->
        {{ inputError }}
    </b-form-invalid-feedback>

    <b-dropdown
      variant="primary"
      :class="[
        'dropdown--primary',
        {
          hide: !showPredictions
        }
      ]"
      :menu-class="{
        show: showPredictions
      }"
      no-caret
    >
      <b-dropdown-item
        class="autocomplete-list-item"
        v-for="(item, index) in predictions"
        :key="item.id"
        :item="item"
        :class="{ selected: curIndex === index }"
        @click="selectLocation(index)"
        @keydown.esc="selectLocation(-1)"
      >
        <span class="autocomplete-list-item-icon"></span>
        <span class="autocomplete-list-item-label">{{ item.description }}</span>
      </b-dropdown-item>
    </b-dropdown>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import geocode from './Geocode'

const KEYCODE = {
  ESC: 27,
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40,
  ENTER: 13,
  SPACE: 32,
  TAB: 9
}

const API_REQUEST_OPTIONS = [
  'bounds',
  'location',
  'component-restrictions',
  'offset',
  'radius',
  'types'
]

export default {
  props: {
    value: String,

    placeholder: String,

    inputState: Boolean,

    inputError: String,

    inputClass: String,

    apiKey: String,

    prependIcon: Boolean,

    baseUri: {
      type: String,
      default: 'https://maps.googleapis.com/maps/api/js'
    },

    componentRestrictions: {
      type: [Boolean, Object, String],
      default: false
    },

    custom: Boolean,

    libraries: {
      type: Array,
      default() {
        return ['geometry', 'places']
      }
    },

    bounds: {
      type: [Boolean, Object, String],
      default: false
    },

    location: {
      type: [Boolean, Object, String],
      default: false
    },

    offset: {
      type: Boolean,
      default: false
    },

    radius: {
      type: Boolean,
      default: false
    },

    types: {
      type: [Boolean, Array],
      default: false
    }
  },

  data() {
    return {
      loaded: false,
      loading: false,
      predictions: false,
      curIndex: -1,
      query: '',
      coordinates: {
        lat: '',
        lng: ''
      },

      hasFocus: false
    }
  },

  computed: {
    showPredictions() {
      return this.hasFocus && this.predictions && this.predictions.length > 0
    }
  },

  methods: {
    ...mapActions({
      addAlert: 'alert/addAlert'
    }),

    onKeyupLocation(e) {
      this.query = e.target.value
      if (!this.query) {
        this.predictions = []
        this.$emit('select', null)
        return
      }

      // traverse prediction list
      if (e.keyCode === KEYCODE.UP && this.curIndex > -1) {
        this.curIndex--
      } else if (
        e.keyCode === KEYCODE.DOWN &&
        this.curIndex < this.predictions.length - 1
      ) {
        this.curIndex++
      } else if (e.keyCode === KEYCODE.ENTER) {
        this.selectLocation(this.curIndex)
      } else {
        // show predictions
        this.search().then(
          response => {
            this.predictions = response
          },
          error => {
            if (error) {
              this.predictions = false
            }
          }
        )
      }
    },

    selectLocation(index) {
      let place = this.predictions[index]
      if (place) {
        this.select(place)
        this.predictions = false
      }
    },

    getRequestOptions() {
      const options = {
        input: this.query
      }
      for (let i in API_REQUEST_OPTIONS) {
        if (this[i] !== undefined || this[i] !== null) {
          options[i] = this[i]
        }
      }
      return options
    },

    select(place) {
      this.setLoading(true)
      geocode({ placeId: place.place_id })
        .then(response => {
          this.query = response[0].formatted_address
          this.coordinates.lat = response[0].geometry.location.lat()
          this.coordinates.lng = response[0].geometry.location.lng()

          this.$emit('input', this.query)
          this.$emit('select', this.coordinates)
          this.setLoading(false)
        })
        .catch(() => {
          this.addAlert({
            state: false,
            label: 'Fail to fetch coordinates of selected location'
          })
          this.setLoading(false)
        })
    },

    search() {
      return new Promise((resolve, reject) => {
        if (!this.query) {
          this.predictions = false
        } else {
          this.$service.getPlacePredictions(
            this.getRequestOptions(),
            (response, status) => {
              switch (status) {
                case window.google.maps.places.PlacesServiceStatus.OK:
                  resolve(response)
                  break
                default:
                  reject(new Error(`Error with status: ${status}`))
              }
            }
          )
        }
      })
    },

    show() {
      this.hasFocus = true
    },

    hide() {
      setTimeout(() => {
        this.hasFocus = false
      }, 200)
    },

    loadPlacesScript() {
      return new Promise(resolve => {
        if (!this.isPlacesScriptLoaded()) {
          let script = document.createElement('script')
          script.setAttribute(
            'src',
            `${this.baseUri}?key=${this.apiKey}&libraries=${this.libraries.join(
              ','
            )}`
          )
          script.async = true
          document.head.appendChild(script)

          script.onload = () => {
            this.loaded = true
            resolve()
          }
        } else {
          resolve()
        }
      })
    },

    setLoading(loading) {
      this.loading = loading
      this.$emit('loading', loading)
    },

    isPlacesScriptLoaded() {
      return (
        typeof window.google === 'object' &&
        typeof window.google.maps === 'object' &&
        typeof window.google.maps.Geocoder === 'function' &&
        typeof window.google.maps.places === 'object' &&
        typeof window.google.maps.places.AutocompleteService === 'function'
      )
    }
  },

  mounted() {
    if (this.apiKey && !this.loaded) {
      this.loadPlacesScript().then(() => {
        this.$geocoder = new window.google.maps.Geocoder()
        this.$service = new window.google.maps.places.AutocompleteService()
        this.loaded = true
        this.$emit('loaded')
      })
    }
  },

  watch: {
    value(value) {
      this.query = value

      // reset location filter
      if (!this.query) {
        this.$emit('select', null)
      }
    }
  }

  /*
    {
        // An array of types specifies an explicit type or a type collection, as listed in the supported types below. If nothing is specified, all types are returned. In general only a single type is allowed. The exception is that you can safely mix the geocode and establishment types, but note that this will have the same effect as specifying no types. The supported types are: geocode instructs the Places service to return only geocoding results, rather than business results. address instructs the Places service to return only geocoding results with a precise address. establishment instructs the Places service to return only business results. the (regions) type collection instructs the Places service to return any result matching the following types: locality sublocality postal_code country administrative_area1 administrative_area2 the (cities) type collection instructs the Places service to return results that match either locality or administrative_area3.
        // Possible values: geocode, address, establishment, cities, locality, sublocality, postal_code, country, administrative_area1, administrative_area2
        type: undefined,

        // is a google.maps.LatLngBounds|google.maps.LatLngBoundsLiteral object specifying the area in which to search for places. The results are biased towards, but not restricted to, places contained within these bounds.
        bounds: undefined,

        // is a boolean specifying whether the API must return only those places that are strictly within the region defined by the given bounds. The API does not return results outside this region even if they match the user input.
        strictBounds: true|false,

        // can be used to restrict results to specific groups. Currently, you can use componentRestrictions to filter by up to 5 countries. Countries must be passed as as a two-character, ISO 3166-1 Alpha-2 compatible country code. Multiple countries must be passed as a list of country codes. z
        componentRestrictions: undefined,

        // can be used to instruct the Autocomplete widget to retrieve only Place IDs. On calling getPlace() on the Autocomplete object, the PlaceResult made available will only have the place id, types and name properties set. You can use the returned place ID with calls to the Places, Geocoding, Directions or Distance Matrix services.
        placeIdOnly: undefined,

        // is a google.maps.LatLng for prediction biasing. Predictions will be biased towards the given location and radius. Alternatively, bounds can be used.
        location: undefined,

        // is a number to determine the character position in the input term at which the service uses text for predictions (the position of the cursor in the input field).
        offset: undefined,

        // is a number to the radius of the area used for prediction biasing. The radius is specified in meters, and must always be accompanied by a location property. Alternatively, bounds can be used.
        radius: undefined
    }
    */
}
</script>

<style lang="scss" scoped>
.autocomplete-field /deep/ {
  position: relative;

  .prepend-icon {
    position: absolute;
    top: 18px;
    left: unset;
    width: 25px;
    margin-left: 8px;
  }

  .input-spinner {
    position: absolute;
    width: 100%;
    height: 100%;

    span {
      color: #efefef !important;
    }
  }

  .search-input {
    color: black;
    border-color: white;
    height: 72px;

    &:hover,
    &:focus {
      border-color: white;
    }

    &:focus::placeholder {
      opacity: 0.3;
    }

    &::placeholder {
      color: black;
    }
  }

  .dropdown {
    .dropdown-toggle {
      display: none;
    }

    .dropdown-menu {
      width: auto;
      padding: 1rem 0;
      box-shadow: var(--box-shadow);
    }
  }

  .dropdown.hide {
    display: none;
  }
}
</style>
